import React from 'react';
import { connect } from 'react-redux';
import loadable from 'react-loadable-visibility/loadable-components';

import {
    fetchSlugInfo,
    fetchAsyncComponents,
    receiveSlugInfo,
    trackPageView,
    loadData,
    loadGraphQLData,
} from './page-actions';
import slugInfoReducer from './slug-info-reducer';
import redirectsReducer from './redirect-reducer';
import ScripLoader from './components/script-loader';
import headerFooterReducer from './components/header-footer/header-footer-reducer';
import scriptLoaderReducer from './components/script-loader/script-loader-reducer';
import Disclaimer from './components/disclaimer';
import DisclaimerV2 from './components/disclaimer-v2';
import MoxieConcierge from './components/moxie-concierge';
import { ModalProvider } from '../context/modal/modal-context';
import PreviewOverlay from './components/preview-overlay';
import SeoMeta from './components/seo-meta';
import SEOLinks from './components/seo-links/seo-links';
import ExitPopup from './components/exit-popup';
import { Helpers } from '../core/src/helpers';
import { withHeaderFooter } from './components/header-footer';
import Breadcrumb from '../shared/components/breadcrumb';
import ErrorRedirect from '../main/components/error-redirect';

import './css/page.less';

const NewFooterStyle = loadable(() => import('./components/header-footer/NewFooterStyle'));

const { memoize } = Helpers;

const parseUrlFilters = memoize(search => {
    return Helpers.getSearch(search);
});
const EMPTY_OBJ = {};

const getFullSlug = (pageDir, pageSlug) => {
    return pageDir ? `${pageDir}${pageSlug ? `/${pageSlug}` : ''}` : '/';
};

const getModelId = modelId => {
    let ids = modelId && modelId.constructor === Array ? modelId.slice(0) : [];
    let last = ids.pop();
    while (!last && ids.length) last = ids.pop();
    return last;
};

/**
 * Returns the page slug based on the router match
 * @param {*} match
 * @returns
 */
export const getDerivedSlug = match => {
    const {
        params: { dir, slug, slugTwo, filters },
    } = match;
    return dir && slug ? `${slug ? slug : ''}${slugTwo ? `/${slugTwo}` : ''}${filters ? `/${filters}` : ''}` : '/';
};

/**
 * Returns the pathname based on the router match
 * @param {*} match
 * @returns
 */
export const getDerivedPath = match => {
    const {
        params: { dir, slug, slugTwo },
    } = match;
    return dir && slug ? `${dir}/${slug}${slugTwo ? `/${slugTwo}` : ''}` : '/';
};

/**
 * @typedef {Object} WithPageProps
 * @property {boolean} customMetaData - True if page template will be defining it's own SEO meta data
 * @property {boolean} disablePageCall - True is page template does not need page API call automatically
 * @property {string} wrapperClassName - custom className for wrapper element
 * @property {boolean} showWordCloud - Render SEO wordcloud
 * @property {boolean} useGraphQL - True if page should use graphql as data source
 * @property {function name(params)} derivedSlug - custom function to format slug for API call
 * @property {function name(params)} derivedPath - custom function to format pathname for client side routing
     
 }}
 */

/**
 * HOC component that should wrap any page template. Handles SEO and page related API calls
 * @example
 * ```jsx
 * withPage(MyTemplateContainer,{ wrapperClassName: 'template-root' })
 * ```
 * @name withPage
 * @param {*} Component
 * @param {WithPageProps} props
 * @returns {ReactElement} The rendered component
 */
const withPage = (Component, props) => {
    let {
        customMetaData = false,
        disablePageCall = false,
        wrapperClassName,
        showWordCloud,
        defaultRedirect,
        disableMobileBreadcrumb,
        useGraphQL = false,
        useV2 = false,
        noProps = false,
        breadcrumbProps,
        asyncComponentTimeOut = 100,
        derivedSlug = getDerivedSlug,
        derivedPath = getDerivedPath,
    } = props || {};
    class WithPage extends React.PureComponent {
        static whyDidYouRender = true;
        componentDidMount() {
            const { slugInfo, search, match } = this.props;
            const {
                params: { dir, slug, slugTwo },
            } = match;
            const pathName = derivedPath(match, search);
            const slugWithFilters = derivedSlug(match, search);

            if (!slugInfo.vanityUrl || slugInfo.vanityUrl !== pathName) {
                this.fetchPage(dir, slugWithFilters, search);
            } else {
                //track page view for SSR
                this.props.trackPageView(slugInfo);
                //fetch async components
                this.fetchAsyncComponents(dir, slugWithFilters);
            }
        }

        componentDidUpdate(prevProps, prevState) {
            let { dir: currentDir, slug: currentSlug } = prevProps.match.params;
            const { match, search } = this.props;
            let { dir: newDir, slug: newSlug } = match.params;
            if (currentDir !== newDir || currentSlug !== newSlug) {
                const slugWithFilters = derivedSlug(match, search);
                this.fetchPage(newDir, slugWithFilters);
            }
        }

        componentWillUnmount = () => {
            if (this.timerHandle) {
                clearTimeout(this.timerHandle);
                this.timerHandle = 0;
            }
        };

        fetchAsyncComponents = (pageDir, pageSlug) => {
            const { slugInfo } = this.props;
            if (useGraphQL && !slugInfo.asyncComponentsReceived) {
                const fullSlug = getFullSlug(pageDir, pageSlug);
                this.timerHandle = setTimeout(() => {
                    this.props.fetchAsyncComponents(fullSlug);
                }, asyncComponentTimeOut);
            }
        };

        fetchPage = (pageDir, pageSlug, search) => {
            const fullSlug = getFullSlug(pageDir, pageSlug);
            let options = {};
            if (disablePageCall) {
                return;
            }
            //pass along preview parameter for preview mode
            if (search && search._preview) {
                options._preview = search._preview;
            }
            if (useGraphQL) {
                options.useGraphQL = true;
            }
            return this.props.fetchSlugInfo(fullSlug, options).then(resp => {
                //fetch async components
                const { slugInfo } = resp || {};
                if (!slugInfo || !slugInfo.asyncComponentsReceived) {
                    this.fetchAsyncComponents(pageDir, pageSlug);
                }
            });
        };

        hasClientRedirect = () => {
            let { redirects } = this.props;
            let { destination } = redirects || {};
            return destination && destination.length > 0;
        };

        getBreadCrumbs = seo => {
            const { pathname, slugInfo } = this.props;
            let { templateKey, components } = slugInfo || {};
            const { product } = components || {};
            if (seo && seo.breadcrumbs && seo.breadcrumbs.length > 0) {
                return seo.breadcrumbs;
            }
            //TODO: For PDP only, at some point this should be an ETR thing
            if (product && templateKey === 'pdp-v2') {
                return undefined;
            }
            return [
                { href: process.env.BASENAME, text: 'Store Home' }, //TODO: figure out how to get this default with the basename
                { href: pathname, text: slugInfo.key },
            ];
        };

        getBreadcrumbProps = () => {
            let { slugInfo } = this.props;
            let { templateKey, components } = slugInfo || {};
            const { product } = components || {};
            if (templateKey === 'dlp-v2') {
                return {
                    showJsonLd: true,
                    unlinkTrailing: true,
                };
            }
            if (templateKey === 'pdp-v2' && product) {
                return {
                    title: product.name,
                    modelId: getModelId(product.modelId),
                    productId: product.prodId !== product.sku ? product.prodId : undefined,
                };
            }
            return {};
        };

        render() {
            if (this.props.pageNotFound || this.hasClientRedirect()) {
                let { redirects } = this.props;
                let { status } = redirects || {};

                let redirectProps = defaultRedirect && status === 404 ? { to: defaultRedirect } : {};
                return <ErrorRedirect {...redirectProps} />;
            }

            const { slugInfo, search, match, siteConfig } = this.props;
            const { seo, previewMode, css, components = {}, templateKey, title } = slugInfo || {};
            const { default: defaultWordCloud } = siteConfig.wordCloud || {};
            const { moxieConcierge, exitPopup } = components;
            const { activeExitPopup } = exitPopup || {};
            const { enableMoxie } = moxieConcierge || {};
            const { hideCopyBlock, copyBlock, wordCloud } = seo || {};

            //TODO: If the new designs are approved this can just be for all pages
            const isV2 =
                templateKey in { 'filters-v2': 1, 'sale-v4': 1, 'compare-v2': 1, 'dlp-v2': 1, 'pdp-v2': 1 } || useV2;
            const isHomepage = templateKey === 'home';
            const allBreadcrumbProps = {
                ...(breadcrumbProps || {}),
                ...this.getBreadcrumbProps(),
            };
            //TODO: change this from a boolean to maybe a list of props that are needed at the template level
            const pageProps = noProps
                ? { match, seo }
                : {
                      ...this.props,
                      slugInfo,
                  };

            return (
                <React.Fragment>
                    {!customMetaData && <SeoMeta seo={seo} previewMode={previewMode} />}
                    {css && <style dangerouslySetInnerHTML={Helpers.createMarkup(css)}></style>}
                    {enableMoxie && <MoxieConcierge />}
                    {activeExitPopup && <ExitPopup exitPopup={activeExitPopup} />}
                    <ModalProvider className={wrapperClassName} name={'content'} id={'content'}>
                        {isV2 && !isHomepage && (
                            <Breadcrumb
                                title={title}
                                match={match}
                                templateKey={templateKey}
                                className={'page-crumbs v2'}
                                customBreadcrumbs={this.getBreadCrumbs(seo)}
                                disableMobileBreadcrumb={disableMobileBreadcrumb}
                                isV2={isV2}
                                {...allBreadcrumbProps}
                            />
                        )}
                        <Component {...pageProps} />
                        {showWordCloud && wordCloud && wordCloud.length > 0 && (
                            <SEOLinks links={wordCloud} useDefaultLinks={true} className={isV2 ? 'v2' : ''} />
                        )}
                    </ModalProvider>
                    {isV2 && (
                        <DisclaimerV2
                            hideSeoParagraph={hideCopyBlock || false}
                            seoParagraph={copyBlock}
                            wordCloud={wordCloud || defaultWordCloud}
                        />
                    )}
                    {!isV2 && <Disclaimer hideSeoParagraph={hideCopyBlock || false} seoParagraph={copyBlock} />}
                    {/* Show this overlay when in preview mode */}
                    {previewMode && <PreviewOverlay slugInfo={slugInfo} previewParam={search && search._preview} />}
                    {isV2 && <NewFooterStyle />}
                </React.Fragment>
            );
        }
    }

    const mapStateToProps = (state, ownProps) => {
        const { slugInfo, storeEnvironment, router, redirects, testFlags, siteConfig, userData } = state;
        const { originCountryCode } = userData || {};
        const { error } = slugInfo || {};
        const { location } = router;
        const search = parseUrlFilters(location.search);
        return {
            redirects,
            slugInfo,
            storeEnvironment,
            pathname: location.pathname,
            search,
            pageNotFound: !!error,
            siteConfig,
            originCountryCode,
        };
    };

    const mapDispatchToProps = dispatch => {
        return {
            fetchSlugInfo: (slug, options) => dispatch(fetchSlugInfo(slug, options)),
            fetchAsyncComponents: (slug, options) => dispatch(fetchAsyncComponents(slug, options)),
            receiveSlugInfo: slugInfo => dispatch(receiveSlugInfo(slugInfo)),
            trackPageView: slugInfo => dispatch(trackPageView(slugInfo)),
        };
    };

    return connect(mapStateToProps, mapDispatchToProps)(WithPage);
};

export default withPage;

export {
    SeoMeta,
    fetchSlugInfo,
    slugInfoReducer,
    redirectsReducer,
    scriptLoaderReducer,
    headerFooterReducer,
    withHeaderFooter,
    ScripLoader,
    loadData,
    loadGraphQLData,
};
